"use client";

/**
 * useDragScroll
 * @description 가로 스크롤 상자를 마우스로 끌어서 넘기게 한다. 터치 기기는 손가락으로 이미
 *   넘길 수 있지만, 마우스는 가로 스크롤 수단이 마땅치 않다(휠은 세로로 간다). 그래서
 *   **마우스일 때만** 끌기를 붙인다 — 터치까지 가로채면 브라우저가 주는 관성 스크롤을 잃는다.
 *
 *   ## 누르자마자 붙잡지 않는다
 *   포인터를 누르는 순간 `setPointerCapture` 하면 그 뒤의 `click` 이 카드가 아니라 **상자**로
 *   간다(캡처 중엔 클릭 타깃이 캡처한 요소가 된다). 그러면 줄 안의 카드를 아무리 눌러도
 *   아무 일도 없다. 그래서 손이 실제로 **움직인 뒤**에야 붙잡고, 끈 뒤에 따라오는 클릭
 *   하나는 삼킨다 — 끌다 놓았는데 카드가 열리면 그것대로 사고다.
 * @example
 * const { ref, handlers } = useDragScroll<HTMLDivElement>();
 * <div ref={ref} {...handlers} className="overflow-x-auto">…</div>
 */
import {
  useCallback,
  useRef,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
} from "react";

interface DragOrigin {
  x: number;
  scrollLeft: number;
  /** 이만큼 움직인 뒤부터 "끌기"다 — 그 전까지는 클릭일 수 있다 */
  dragging: boolean;
}

/** 클릭과 끌기를 가르는 거리(px) — 손이 흔들리는 정도는 클릭으로 본다 */
const DRAG_THRESHOLD_PX = 4;

export interface DragScrollHandlers<E extends HTMLElement> {
  onPointerDown: (event: PointerEvent<E>) => void;
  onPointerMove: (event: PointerEvent<E>) => void;
  onPointerUp: (event: PointerEvent<E>) => void;
  onPointerCancel: (event: PointerEvent<E>) => void;
  onClickCapture: (event: MouseEvent<E>) => void;
}

export function useDragScroll<E extends HTMLElement>(): {
  ref: RefObject<E | null>;
  handlers: DragScrollHandlers<E>;
} {
  const ref = useRef<E>(null);
  /** 끌기 시작 지점과 그때의 스크롤 위치. 렌더와 무관해 ref 에 둔다. */
  const origin = useRef<DragOrigin | undefined>(undefined);
  /** 방금 끌기를 끝냈다 — 따라오는 클릭 하나를 삼킨다 */
  const suppressClick = useRef(false);

  const onPointerDown = useCallback((event: PointerEvent<E>) => {
    if (event.pointerType !== "mouse" || event.button !== 0 || !ref.current)
      return;
    origin.current = {
      x: event.clientX,
      scrollLeft: ref.current.scrollLeft,
      dragging: false,
    };
  }, []);

  const onPointerMove = useCallback((event: PointerEvent<E>) => {
    const start = origin.current;
    if (!start || !ref.current) return;
    const dx = event.clientX - start.x;
    if (!start.dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      start.dragging = true;
      // 포인터를 붙잡아 두면 요소 밖으로 나가도 끌기가 이어진다.
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    // 끄는 방향과 내용이 움직이는 방향은 반대다.
    ref.current.scrollLeft = start.scrollLeft - dx;
  }, []);

  const endDrag = useCallback((event: PointerEvent<E>) => {
    const start = origin.current;
    if (!start) return;
    origin.current = undefined;
    if (start.dragging) suppressClick.current = true;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const onClickCapture = useCallback((event: MouseEvent<E>) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    ref,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onClickCapture,
    },
  };
}
